/**
 * The offline demo (`?demo`).
 *
 * A visitor with a link and no account gets a complete exercise: the real turn
 * machine, the real projection, the real Missile Warning staged reveal — all of
 * it running **in this browser tab**, in a worker, with no server involved.
 *
 * ## Why this exists at all
 *
 * The deployed game needs a VM, a database, sixteen people and a code somebody
 * issued. None of that is available to someone opening a link out of curiosity,
 * and every part of it can be down. The demo removes the whole dependency: it
 * cannot fail because the API is unreachable, it cannot collide with another
 * visitor, and it costs nothing to run.
 *
 * ## What it is not
 *
 * It is not a mock. `packages/server/src/game/` is imported wholesale and run as
 * it ships — the same `GameService`, `TurnMachine`, `Broadcaster`, `MwScheduler`
 * and seat-session rules the VM runs (see that package's `index.ts` for the two
 * lint rules that keep it importable here). What the demo supplies is only what a
 * platform must: entropy, a store, a hash, tokens, and a transport.
 *
 * ## The passphrase is not a secret
 *
 * There is no server to guess against and no other player to hide from: the game
 * lives in the visitor's own tab and dies with it. The passphrase exists because
 * the entry flow is the product being shown, so `?demo` prefills it rather than
 * skipping the screen. Making it a build-time variable would imply a secret that
 * is not one.
 */

/** `BSNT-DEMO` fits `zGameCode`'s `BSNT-[A-Z0-9]{4}` (§3.1) — deliberately. */
export const DEMO_CODE = 'BSNT-DEMO';

export const DEMO_PASSPHRASE = 'badger-demo-orbit-console';

/** The scenario the demo runs. `scenarios/demo.json`, by name. */
export const DEMO_SCENARIO = 'First Contact (Demo)';

/**
 * A base URL no network stack will ever resolve.
 *
 * Every REST call in the client already funnels through `api/games.ts`'s `call()`
 * and builds its URL from the configured base, so a sentinel scheme is all it
 * takes to route the demo's requests into the worker instead of onto the network
 * — with no call site changed and no second REST client.
 */
export const DEMO_BASE_URL = 'demo://badger-sentinel';
export const DEMO_WS_URL = 'demo://badger-sentinel/ws';

/** True when this page load asked for the demo (`?demo`, or `?demo=1`). */
export function isDemoRequested(search: string = globalThis.location?.search ?? ''): boolean {
  try {
    return new URLSearchParams(search).has('demo');
  } catch {
    return false;
  }
}

export function isDemoUrl(url: string): boolean {
  return url.startsWith('demo://');
}
